import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { MapPinIcon, UserIcon, HomeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import type { Booking } from '../../types/Dashboard';
import Button from '../Button';

interface BookingFormProps {
  onSubmit: (data: Partial<Booking>) => void;
  initialData?: Partial<Booking>;
  onCancel: () => void;
}

const BookingForm: React.FC<BookingFormProps> = ({
  onSubmit,
  initialData,
  onCancel
}) => {
  const { register, handleSubmit, control, formState: { errors } } = useForm<Partial<Booking>>({
    defaultValues: initialData || {
      status: 'upcoming',
      totalAmount: 0,
      numberOfGuests: 1
    }
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="guestName" className="block text-sm font-medium text-gray-700">
            Guest Name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              {...register('guestName', { required: 'Guest name is required' })}
              className={`block w-full pl-10 pr-3 py-2 border ${errors.guestName ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
              placeholder="Enter guest name"
            />
          </div>
          {errors.guestName && (
            <p className="mt-1 text-sm text-red-600">{errors.guestName.message}</p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="propertyName" className="block text-sm font-medium text-gray-700">
            Property
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <HomeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              {...register('propertyName', { required: 'Property name is required' })}
              className={`block w-full pl-10 pr-3 py-2 border ${errors.propertyName ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
              placeholder="Select property"
            />
          </div>
          {errors.propertyName && (
            <p className="mt-1 text-sm text-red-600">{errors.propertyName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700">
            Location
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPinIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              {...register('location', { required: 'Location is required' })}
              className={`block w-full pl-10 pr-3 py-2 border ${errors.location ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
              placeholder="Enter location"
            />
          </div>
          {errors.location && (
            <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="numberOfGuests" className="block text-sm font-medium text-gray-700">
            Number of Guests
          </label>
          <div className="mt-1">
            <input
              type="number"
              {...register('numberOfGuests', { 
                required: 'Number of guests is required',
                min: { value: 1, message: 'Minimum 1 guest' }
              })}
              className={`block w-full px-3 py-2 border ${errors.numberOfGuests ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
              min="1"
            />
          </div>
          {errors.numberOfGuests && (
            <p className="mt-1 text-sm text-red-600">{errors.numberOfGuests.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="checkIn" className="block text-sm font-medium text-gray-700">
            Check-in Date
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <Controller
              control={control}
              name="checkIn"
              rules={{ required: 'Check-in date is required' }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.checkIn ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Select check-in date"
                />
              )}
            />
          </div>
          {errors.checkIn && (
            <p className="mt-1 text-sm text-red-600">{errors.checkIn?.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="checkOut" className="block text-sm font-medium text-gray-700">
            Check-out Date
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <Controller
              control={control}
              name="checkOut"
              rules={{ required: 'Check-out date is required' }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  className={`block w-full pl-10 pr-3 py-2 border ${errors.checkOut ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Select check-out date"
                  minDate={new Date()}
                />
              )}
            />
          </div>
          {errors.checkOut && (
            <p className="mt-1 text-sm text-red-600">{errors.checkOut?.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700">
            Total Amount
          </label>
          <div className="mt-1">
            <input
              type="number"
              {...register('totalAmount', { 
                required: 'Total amount is required',
                min: { value: 0, message: 'Amount must be positive' }
              })}
              className={`block w-full px-3 py-2 border ${errors.totalAmount ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
              placeholder="0.00"
            />
          </div>
          {errors.totalAmount && (
            <p className="mt-1 text-sm text-red-600">{errors.totalAmount.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <div className="mt-1">
            <select
              {...register('status', { required: 'Status is required' })}
              className={`block w-full px-3 py-2 border ${errors.status ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-airbnb-red focus:border-transparent`}
            >
              <option value="upcoming">Upcoming</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {errors.status && (
            <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-4">
        <Button
          variant="outline"
          onClick={onCancel}
          type="button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
        >
          {initialData ? 'Update Booking' : 'Create Booking'}
        </Button>
      </div>
    </form>
  );
};

export default BookingForm;